import { useCallback, useEffect, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelectPlan } from "../../CustomHooks/Mutations";
import { useDeals, useStakeDebit, useUnclaimedTransactions } from "../../CustomHooks/Queries";
import { useAppUtilityContext } from "../../Contexts/AppUtilityContext";
import {
  BATTLECARD_FLOW_NAV_STATE,
  BATTLECARD_VERIFICATION_REJECTED_LEGACY_SUBTITLE,
  dispatchAPIErrorToast,
  formatBasicSpendingQualifierForRebateFromDealsResponse,
  getBattlecardCashBackAmount,
  getBattlecardPageSubtitle,
  getMoveInCashPlanRoute,
  withoutFidelDebitCashbackFeature,
} from "../../Utils/helpers";
import BattlecardHeader from "./battlecard-header";
import "./battle-cards.scss";

const LEGACY_FEATURES_BASE = [
  { label: "Cash Back on rent", check: false },
  { label: "2x Cash Back on Stake Debit", sublabel: "At select merchants", check: false },
  { label: "Build credit on rent", check: false },
  { label: "Get paid early", check: false },
];

const BASIC_FEATURES_BASE = [
  { label: "Cash Back on rent", check: false },
  { label: "2x Cash Back on Stake Debit", sublabel: "At select merchants", check: true },
  { label: "Build credit on rent", check: false },
  { label: "Get paid early", check: false },
];

const UNLIMITED_FEATURES_BASE = [
  { label: "Cash Back on rent", badge: "$120/year", badgeType: "green" },
  { label: "2x Cash Back on Stake Debit", sublabel: "At select merchants", check: true },
  { label: "Build credit on rent", check: true },
  { label: "Get paid early", badge: "2 days early", badgeType: "green" },
];

const MOVE_IN_BONUS_BASIC = {
  label: "Move-In Bonus",
  badgeType: "green",
  sublabelTone: "light",
  labelGroupGap: true,
};

const MOVE_IN_BONUS_UNLIMITED = {
  label: "Move-In Bonus",
  sublabel: "After paying rent with Stake",
  bold: true,
  badgeType: "green",
  sublabelTone: "light",
  labelGroupGap: true,
};

function CheckIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <circle cx="8" cy="8" r="7" fill="#1A3D26" />
      <path d="M5 8L7 10L11 6" stroke="#54FF99" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function XIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <circle cx="8" cy="8" r="7" fill="#3A2020" />
      <path d="M5.5 5.5L10.5 10.5M10.5 5.5L5.5 10.5" stroke="#FF6B6B" strokeWidth="1.4" strokeLinecap="round" />
    </svg>
  );
}

function FeatureRow({ feature }) {
  return (
    <div className={`bc-feature-row${feature.sublabel ? " multi-line" : ""}`}>
      <div
        className={
          feature.sublabel
            ? `bc-feature-label-group${feature.labelGroupGap ? " bc-feature-label-group--spaced" : ""}`
            : ""
        }
      >
        <span className={`bc-feature-label${feature.bold ? " bold" : ""}`}>{feature.label}</span>
        {feature.sublabel && (
          <span className={`bc-feature-sublabel${feature.sublabelTone === "light" ? " light" : ""}`}>
            {feature.sublabel}
          </span>
        )}
      </div>
      {feature.badge && <span className={`bc-badge ${feature.badgeType}`}>{feature.badge}</span>}
      {feature.check === true && <CheckIcon />}
      {feature.check === false && <XIcon />}
    </div>
  );
}

export default function BattleCards() {
  const navigate = useNavigate();
  const location = useLocation();
  const hideLegacyFromNav = location.state?.hideLegacy === true;
  const hideBasicAndUnlimited = location.state?.hideBasicAndUnlimited === true;
  const { setAppUtilityProps } = useAppUtilityContext();
  const { isLoading: isLoadingDeals, data: dealsData } = useDeals();
  const { data: stakeDebitData } = useStakeDebit();
  const { data: unclaimedTransactionsData } = useUnclaimedTransactions();

  const moveInCashPlan = useMemo(
    () => dealsData?.data?.data?.currentDeal?.move_in_cash_plan,
    [dealsData],
  );
  const isCurrentLegacyPlan = moveInCashPlan === "legacy";
  const showLegacyCard =
    !hideLegacyFromNav ||
    moveInCashPlan === "legacy" ||
    moveInCashPlan === "basic" ||
    moveInCashPlan === "unlimited";
  const hideLegacy = !showLegacyCard;
  const skipPlanRouteRedirect = hideLegacyFromNav || hideBasicAndUnlimited;

  const moveInCashPlanRoute = useMemo(() => {
    if (isLoadingDeals) return undefined;
    if (moveInCashPlan === "basic") return "/battlecard/start-shopping";
    if (moveInCashPlan === "unlimited") return "/battlecard/pay-rent";
    return null;
  }, [isLoadingDeals, moveInCashPlan]);

  const pageSubtitle = useMemo(
    () => getBattlecardPageSubtitle(dealsData, stakeDebitData),
    [dealsData, stakeDebitData]
  );

  const isVerificationRejectedLegacyView =
    pageSubtitle === BATTLECARD_VERIFICATION_REJECTED_LEGACY_SUBTITLE;
  const pageTitle = "Your Move-In Cash is waiting for you.";
  const pageSubtitleText = isVerificationRejectedLegacyView
    ? pageTitle
    : pageSubtitle;

  const cashBackAmount = useMemo(
    () => getBattlecardCashBackAmount(dealsData, unclaimedTransactionsData),
    [dealsData, unclaimedTransactionsData]
  );

  const legacyFeatures = useMemo(
    () =>
      withoutFidelDebitCashbackFeature(
        [
          { label: `${cashBackAmount} Move-In Bonus`, badge: "4-5 months", badgeType: "red" },
          ...LEGACY_FEATURES_BASE,
        ],
        dealsData
      ),
    [cashBackAmount, dealsData]
  );

  const basicSpendingQualifier = useMemo(
    () => formatBasicSpendingQualifierForRebateFromDealsResponse(dealsData),
    [dealsData]
  );

  const basicFeatures = useMemo(
    () =>
      withoutFidelDebitCashbackFeature(
        [
          {
            ...MOVE_IN_BONUS_BASIC,
            sublabel: `After spending ${basicSpendingQualifier} on Stake Debit`,
            badge: `${cashBackAmount} Cash Back`,
          },
          ...BASIC_FEATURES_BASE,
        ],
        dealsData
      ),
    [cashBackAmount, basicSpendingQualifier, dealsData]
  );

  const unlimitedFeatures = useMemo(
    () =>
      withoutFidelDebitCashbackFeature(
        [{ ...MOVE_IN_BONUS_UNLIMITED, badge: `Instant ${cashBackAmount}` }, ...UNLIMITED_FEATURES_BASE],
        dealsData
      ),
    [cashBackAmount, dealsData]
  );

  const { mutate: selectPlan, isLoading: isSelectingPlan } = useSelectPlan({
    onError: (err) => {
      dispatchAPIErrorToast({
        message: err?.response?.data?.error?.msg ?? err?.response?.data?.msg,
      });
    },
  });

  const handlePlanSelect = useCallback(
    (plan, navigateTo) => {
      selectPlan(
        { plan },
        {
          onSuccess: (response) => {
            const selectedPlan = response?.data?.data?.selected_plan;
            if (selectedPlan === "unlimited") {
              navigate("/battlecard/pay-rent", { state: BATTLECARD_FLOW_NAV_STATE });
              return;
            }
            navigate(navigateTo, { state: BATTLECARD_FLOW_NAV_STATE });
          },
        },
      );
    },
    [navigate, selectPlan],
  );

  const handleCurrentLegacyPlanClick = useCallback(() => {
    const legacyRoute = getMoveInCashPlanRoute(dealsData);

    // Delivered checks have no status page to return to, so fall back to history.
    if (!legacyRoute) {
      navigate(-1);
      return;
    }

    navigate(legacyRoute, { replace: true, state: BATTLECARD_FLOW_NAV_STATE });
  }, [dealsData, navigate]);

  useEffect(() => {
    setAppUtilityProps((p) => ({ ...p, hideNavigationBar: true, lockedRoutes: ["/deals", "/cash"] }));
    return () => setAppUtilityProps((p) => ({ ...p, lockedRoutes: [] }));
  }, [setAppUtilityProps]);

  useEffect(() => {
    if (moveInCashPlanRoute && !skipPlanRouteRedirect) {
      navigate(moveInCashPlanRoute, { replace: true, state: BATTLECARD_FLOW_NAV_STATE });
    }
  }, [moveInCashPlanRoute, navigate, skipPlanRouteRedirect]);

  if ((isLoadingDeals || moveInCashPlanRoute) && !skipPlanRouteRedirect) {
    return (
      <div className="battle-cards-page">
        <BattlecardHeader />
      </div>
    );
  }

  return (
    <div className="battle-cards-page">
      <BattlecardHeader />
      <div className="bc-scene active">
        <h1
          className={`bc-page-title${
            isVerificationRejectedLegacyView ? " bc-page-title--rejected-legacy" : ""
          }`}
        >
          {isVerificationRejectedLegacyView ? (
            <>
              <span className="bc-page-title-line">
                Since the verification was not approved,
              </span>
              <span className="bc-page-title-line">
                please proceed with a legacy request.
              </span>
            </>
          ) : (
            pageTitle
          )}
        </h1>
        <p className="bc-page-subtitle">{pageSubtitleText}</p>

        <div
          className={`bc-cards-container${
            hideLegacy ? " bc-cards-container--two-cards" : ""
          }${hideBasicAndUnlimited ? " bc-cards-container--one-card" : ""}`}
        >
          {showLegacyCard && (
            <div className="bc-tier-card bc-tier-card--legacy">
              <div className="bc-card-header">
                <div className="bc-card-plan-name">Legacy</div>
                <div className="bc-card-subtitle">Landlord approves a physical check</div>
              </div>
              <div className="bc-features-wrap">
                <div className="bc-features">
                  {legacyFeatures.map((f, i) => (
                    <FeatureRow key={i} feature={f} />
                  ))}
                </div>
              </div>
              <div className="bc-card-footer">
                <button
                  type="button"
                  className="bc-card-btn dark"
                  disabled={isSelectingPlan}
                  onClick={
                    isCurrentLegacyPlan
                      ? handleCurrentLegacyPlanClick
                      : () => handlePlanSelect("legacy", "/battlecard/legacy-terms")
                  }
                >
                  {isCurrentLegacyPlan ? "Current Plan" : "Request Check"}
                </button>
                <div className="bc-est-time">Est time: 4-5 months</div>
              </div>
            </div>
          )}

          {!hideBasicAndUnlimited && (
            <>
              <div className="bc-tier-card bc-tier-card--basic">
                <div className="bc-card-header">
                  <div className="bc-card-plan-name">Basic</div>
                  <div className="bc-card-subtitle">Cash Back for shopping</div>
                </div>
                <div className="bc-features-wrap">
                  <div className="bc-features">
                    {basicFeatures.map((f, i) => (
                      <FeatureRow key={i} feature={f} />
                    ))}
                  </div>
                </div>
                <div className="bc-card-footer">
                  <button
                    type="button"
                    className="bc-card-btn primary"
                    disabled={isSelectingPlan}
                    onClick={() => handlePlanSelect("basic", "/battlecard/start-shopping")}
                  >
                    {isCurrentLegacyPlan ? "Switch Plan" : "Start Shopping"}
                  </button>
                  <div className="bc-est-time">Est time: 1 minute</div>
                </div>
              </div>

              <div className="bc-tier-card bc-tier-card--unlimited highlight">
                <div className="bc-card-header">
                  <div className="bc-card-plan-name green">Unlimited</div>
                  <div className="bc-card-subtitle">Cash Back for shopping and paying rent</div>
                </div>
                <div className="bc-features-wrap">
                  <div className="bc-features">
                    {unlimitedFeatures.map((f, i) => (
                      <FeatureRow key={i} feature={f} />
                    ))}
                  </div>
                </div>
                <div className="bc-card-footer">
                  <button
                    type="button"
                    className="bc-card-btn primary"
                    disabled={isSelectingPlan}
                    onClick={() => handlePlanSelect("unlimited", "/battlecard/pay-rent")}
                  >
                    {isCurrentLegacyPlan ? "Switch Plan" : "Pay Rent"}
                  </button>
                  <div className="bc-est-time">Est time: 2 minutes</div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
